import React, { FC, ReactElement, useMemo } from 'react';
import { removeLeadingNonAlphanumeric } from 'utils/StringUtils';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import useCollections from 'networking/mindfeed/hooks/useCollections';
import { Collection, BoxViewCard } from './Collection';
import { BOX_VIEWS } from 'models/BoxView';
import { Collection as CollectionModel } from 'models/Collection';
import SectionLabel from 'elements/SectionLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useMemoryCreation } from 'hooks/useMemoryCreation';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';

interface Props {
  onCollectionSelect?: () => void;
  onCollectionEdit?: () => void;
  onCollectionDelete?: () => void;
  // The views as rows like the collections, instead of cards: for the
  // narrow switcher panel.
  viewsAsRows?: boolean;
}

const CollectionList: FC<Props> = React.memo(
  ({
    onCollectionSelect,
    onCollectionEdit,
    onCollectionDelete,
    viewsAsRows = false,
  }): ReactElement => {
    const { data: collections } = useCollections();
    const { selectedCollection, selectedView } = useActiveSpace();
    // A view row lights up only while no collection is open: a collection
    // shows its memos like the Timeline, but it is the collection's row.
    const isViewSelected = (view: typeof selectedView) =>
      !selectedCollection && selectedView === view;
    const { createCollection } = useMemoryCreation();
    const t = useTranslations('collections');

    // The row components own selecting + navigating (via useSpaceNavigation);
    // `onCollectionSelect` here is just the "row activated" side-effect, e.g.
    // closing the switcher popover.

    const sortedCollections = useMemo(() => {
      if (!collections) return [];

      // Create a collator with numeric sorting enabled. We use Intl.Collator with the numeric
      // option to sort numbers in strings naturally (e.g., 'Collection 2' before 'Collection 10')
      const collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
      });

      const compareNaturally = (a: CollectionModel, b: CollectionModel) => {
        const nameA = removeLeadingNonAlphanumeric(a.name).trim();
        const nameB = removeLeadingNonAlphanumeric(b.name).trim();
        return collator.compare(nameA, nameB);
      };

      return [...collections].sort(compareNaturally);
    }, [collections]);

    return (
      <div className="space-y-1">
        {/* The Timeline across the top, the other views as cards under
            their own label. */}
        <BoxViewCard
          view="timeline"
          layout={viewsAsRows ? 'row' : 'wide'}
          isSelected={isViewSelected('timeline')}
          onSelect={onCollectionSelect}
        />
        <SectionLabel className={styles.label}>{t('quickAccess')}</SectionLabel>
        <div className={viewsAsRows ? styles.viewRows : styles.viewCards}>
          {BOX_VIEWS.filter((view) => view !== 'timeline').map((view) => (
            <BoxViewCard
              key={view}
              view={view}
              layout={viewsAsRows ? 'row' : 'compact'}
              isSelected={isViewSelected(view)}
              onSelect={onCollectionSelect}
            />
          ))}
        </div>

        <SectionLabel className={styles.label}>
          {t('sectionLabel')}
        </SectionLabel>
        {sortedCollections.length > 0 ? (
          sortedCollections.map((collection) => {
            const isSelected = selectedCollection?.id === collection.id;

            return (
              <Collection
                key={collection.id}
                collection={collection}
                isSelected={isSelected}
                onSelect={onCollectionSelect}
                onEdit={onCollectionEdit}
                onDelete={onCollectionDelete}
              />
            );
          })
        ) : (
          <div className={styles.empty.container}>
            <p className={styles.empty.text}>{t('empty')}</p>
            <button
              type="button"
              className={styles.empty.button}
              onClick={createCollection}
            >
              <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
              {t('createOne')}
            </button>
          </div>
        )}
      </div>
    );
  },
);

export default CollectionList;

const styles = {
  viewCards: 'grid grid-cols-3 gap-2 pt-1',
  viewRows: 'space-y-1',
  label: 'px-3 pt-3 pb-1 uppercase',
  empty: {
    container: 'px-3 pt-2 space-y-3.5',
    text: 'text-xs text-ink-secondary',
    // Accent tint pair: pale blue on white, translucent blue in dark.
    button: classNames(
      'inline-flex items-center gap-1.5 rounded-full bg-accent-tint px-4 py-2.5',
      'text-sm font-semibold text-accent-tint-ink transition-opacity hover:opacity-80',
    ),
  },
};
